<script setup lang="ts" generic="T extends ApiLogisticsTaskGetById | ApiPublicTransportationCompanyDriveGetLogisticsTaskByCuid">
import type { ApiLogisticsTaskGetById, ApiPublicTransportationCompanyDriveGetLogisticsTaskByCuid } from '~/types'

const props = defineProps<{
  logisticsTask: T
  viewOnly?: boolean
  disabled?: boolean
}>()

const emits = defineEmits<{ (e: 'save', payload: T): void }>()

const { cloned } = useMightyCloned(props.logisticsTask)

function submit() {
  emits('save', cloned.value)
}

const hasDisabledPosition = computed(() => props.logisticsTask.positionsToShip.some((position) => {
  return !position.isIssued || position.isDelivered
}))

const doesTransportationCompanyDoTask = computed(() => props.logisticsTask.doesTransportationCompanyDoTask)
const isOutbound = computed(() => props.logisticsTask.type === 'outbound')

// Automatically fill in Transportation Company Driver name and license plate number with previously reported values
// https://github.com/sidestream-tech/hanselmann-os/issues/5066
cloned.value.transportationCompanyDriverName ??= cloned.value.reportedTransportationCompanyDriverName
cloned.value.transportationCompanyLicensePlateNumber ??= cloned.value.reportedTransportationCompanyLicensePlateNumber

const formkitPlugins = useFormkitPlugins()
</script>

<template>
  <template v-if="viewOnly">
    <div
      v-for="position of logisticsTask.positionsToShip"
      :key="position.id"
    >
      <div class="flex items-center gap-2">
        <Icon v-if="position.isLoaded" name="material-symbols:check-box-outline-rounded" class="text-green-500" />
        <Icon v-else name="material-symbols:check-box-outline-blank" />
        <span>{{ useMakeLabelForPosition(position) }}</span>
      </div>
    </div>
  </template>
  <div v-else class="flex flex-col gap-4">
    <n-alert v-if="hasDisabledPosition" type="warning">
      {{ $t('logisticsTask.loadingChecklist.alreadyDelivered') }}
    </n-alert>
    <FormKit
      v-model="cloned"
      type="form"
      :plugins="[formkitPlugins]"
      submit-label="Speichern"
      :disabled
      @submit="submit"
    >
      <FormKit
        v-model="cloned.positionsToShip"
        type="list"
      >
        <FormKit
          v-for="(position, index) of cloned.positionsToShip"
          :key="index"
          type="group"
        >
          <FormKit
            id="isLoaded"
            type="checkbox"
            :label="useMakeLabelForPosition(position)"
            :disabled="!position.isIssued || position.isDelivered"
          />
        </FormKit>
      </FormKit>
      <template v-if="doesTransportationCompanyDoTask && isOutbound">
        <FormKit
          id="transportationCompanyDriverName"
          :label="$t('logisticsTask.transportationCompanyDriverName.label')"
          type="text"
        />
        <FormKitSignaturePopup
          id="transportationCompanyDriverSignature"
          :title="$t('logisticsTask.transportationCompanyDriverSignature.label')"
          :label="$t('logisticsTask.transportationCompanyDriverSignature.signature.label')"
          :button-text="$t('logisticsTask.transportationCompanyDriverSignature.buttonText')"
          :default="undefined"
          disable-by-pass
          disable-positions-overview
        />
        <FormKit
          id="transportationCompanyLicensePlateNumber"
          :label="$t('logisticsTask.transportationCompanyLicensePlateNumber.label')"
          type="text"
        />
      </template>
    </FormKit>
  </div>
</template>
